import { useCallback, useEffect, useState } from "react";

/**
 * 표 밀도 (설계원칙 P1·P5·P7·P8).
 *
 * 데이터 밀도가 필요한 제품의 기본값은 compact 여야 한다는 것이 SAP Fiori 의 명시적 권고이고,
 * 이 시스템의 기본값(행 32px)이 이미 그 값이다. 여기서 하는 일은 **사용자가 위아래로 한 단계씩
 * 바꿀 수 있게** 하는 것뿐이다 — 하루 종일 표를 보는 담당자는 대개 더 조밀한 쪽을 고르고,
 * 가끔 들어오는 사람은 여유 있는 쪽이 편하다.
 *
 * 값은 `<html data-density>` 에 걸린다. 표마다가 아니라 **애플리케이션에 한 번만** 두는 것이
 * Fiori 의 요구다(한 페이지 안에서 밀도를 섞지 않는다). 그래서 컨트롤도 상단바에 하나뿐이고,
 * 실제 여백은 `index.css` 의 `--hj-*` 변수가 정한다.
 *
 * 저장은 localStorage 다. 밀도는 계정이 아니라 **그 사람이 쓰는 화면**의 성질이라
 * 서버에 둘 값이 아니다.
 */

export const DENSITIES = ["조밀", "보통", "여유"] as const;
export type Density = (typeof DENSITIES)[number];

/** 32px — Fiori compact · Carbon sm. 원칙이 권하는 데스크톱 진입 기본값 */
const DEFAULT_DENSITY: Density = "보통";
const STORAGE_KEY = "hj.density";

function isDensity(value: string | null): value is Density {
  return value !== null && (DENSITIES as readonly string[]).includes(value);
}

function readStored(): Density {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isDensity(stored) ? stored : DEFAULT_DENSITY;
  } catch {
    // 사파리 프라이빗 모드 등에서 localStorage 접근이 던진다. 기본값으로 계속 간다.
    return DEFAULT_DENSITY;
  }
}

/**
 * 화면이 그려지기 전에 한 번 적용한다.
 *
 * 훅이 도는 시점까지 기다리면 기본 밀도로 한 번 그려진 뒤 바뀌어 표가 눈에 띄게 튄다.
 * `main.tsx` 에서 부른다.
 */
export function applyStoredDensity() {
  document.documentElement.dataset.density = readStored();
}

export function useDensity(): { density: Density; setDensity: (value: Density) => void } {
  const [density, setState] = useState<Density>(readStored);

  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  const setDensity = useCallback((value: Density) => {
    setState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // 저장에 실패해도 이번 세션 동안은 적용된다.
    }
  }, []);

  return { density, setDensity };
}
